import React from 'react';
import { toast } from 'react-toastify';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { STATUS_ACCENTS, DUPLICATED, REMOVED } from '../theme/oceanTokens';

/**
 * What a notification *means*, not just whether it worked.
 *
 * "Pack deleted" and "Pack duplicated" are both successes, so as plain
 * `toast.success` calls they came out as the same green bar with the same tick
 * — two different outcomes that looked identical. Each intent below gets its
 * own tone and its own glyph, so the difference is legible at a glance:
 * creating is green, copying is cyan, editing is indigo, and a deletion is
 * a flat red — the instinctive colour, told apart from the error toast by
 * being solid where that one is a gradient, and by its own glyph.
 *
 * Every tone is a token from the theme, so a palette change carries through
 * here too — mostly the status accents, plus DUPLICATED and REMOVED, which
 * the status set had no equivalent for.
 */
export const TOAST_INTENTS = {
  created: { type: 'success', tone: STATUS_ACCENTS.completed, glyph: AddRoundedIcon },
  duplicated: { type: 'success', tone: DUPLICATED, glyph: ContentCopyRoundedIcon },
  updated: { type: 'success', tone: STATUS_ACCENTS.confirmed, glyph: EditRoundedIcon },
  deleted: { type: 'success', tone: REMOVED, glyph: DeleteOutlineRoundedIcon },
  failed: { type: 'error', tone: STATUS_ACCENTS.cancelled, glyph: CloseRoundedIcon },
  warned: { type: 'warning', tone: STATUS_ACCENTS.booked, glyph: PriorityHighRoundedIcon },
  told: { type: 'info', tone: STATUS_ACCENTS.total, glyph: InfoRoundedIcon },
};

/**
 * Fires a toast carrying its intent.
 *
 * The class drives the tone (AppToasts defines one rule per intent) and the
 * per-toast `icon` beats the container's default, which is what lets a
 * success say which kind of success it was.
 */
const fire = (intent) => (message, options = {}) => {
  const { type, glyph: Glyph } = TOAST_INTENTS[intent];
  return toast[type](message, {
    ...options,
    className: `pymed-intent--${intent}`,
    icon: (
      <span className="pymed-toast-glyph">
        <Glyph sx={{ fontSize: 17 }} />
      </span>
    ),
  });
};

/**
 * Use these instead of `toast.*` where the outcome has a shape: something was
 * added, copied, changed or removed. Plain `toast.success` / `toast.error`
 * still work everywhere and keep the generic treatment.
 */
export const notify = {
  created: fire('created'),
  duplicated: fire('duplicated'),
  updated: fire('updated'),
  deleted: fire('deleted'),
  failed: fire('failed'),
  warned: fire('warned'),
  told: fire('told'),
};

export default notify;
